import React from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Clock, Building2, MapPin, Tag, User as UserIcon, FileText, Briefcase, Pause as PauseIcon, Globe2 } from "lucide-react";
import { formatDateFr } from "../lib/dateFormat";
import { formatTimezoneLabel, timezoneFull } from "../lib/timezone";

// Helpers : calcul des heures effectives (durée totale - pauses non rémunérées).
function hhmmToMin(t) { const [h, m] = (t || "0:0").split(":").map(Number); return h * 60 + m; }
function shiftMinutes(start, end) {
  let d = hhmmToMin(end) - hhmmToMin(start);
  if (d < 0) d += 24 * 60;
  return d;
}
function formatMinutes(min) {
  const h = Math.floor(min / 60); const m = min % 60;
  return m === 0 ? `${h}h` : `${h}h${String(m).padStart(2, "0")}`;
}

/**
 * Modale "Consulter" — affichage détaillé d'un shift.
 * Props:
 *  - open / onOpenChange
 *  - shift: objet shift (avec date, start_time, end_time, role_label, notes…)
 *  - brand: département
 *  - site: site
 *  - category: catégorie de shift
 *  - collab: collaborateur (optionnel)
 */
export default function ShiftDetailsDialog({ open, onOpenChange, shift, brand, site, category, collab }) {
  if (!shift) return null;
  const Row = ({ icon: Icon, label, value, valueTestId }) => (
    <div className="flex items-start gap-3">
      <Icon size={14} className="text-zinc-400 mt-0.5 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="label-uppercase mb-0.5">{label}</p>
        <p className="text-sm text-zinc-900 break-words" data-testid={valueTestId}>{value || <span className="text-zinc-400 font-mono text-xs">—</span>}</p>
      </div>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex flex-col max-h-[90vh] p-0 sm:max-w-lg" data-testid="shift-details-dialog">
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-zinc-100">
          <DialogTitle>Détails du shift</DialogTitle>
          <p className="text-xs text-zinc-500 font-mono mt-1">
            {formatDateFr(shift.date)} · {shift.start_time}–{shift.end_time}
          </p>
        </DialogHeader>
        <div className="overflow-y-auto px-6 py-5 flex-1 space-y-4">
          {collab && (
            <Row icon={UserIcon} label="Collaborateur" value={collab.name} valueTestId="sd-collab" />
          )}
          <Row icon={Clock} label="Horaires" value={`${shift.start_time} → ${shift.end_time}`} valueTestId="sd-hours" />
          {brand?.timezone && (() => {
            const tz = formatTimezoneLabel(brand.timezone);
            return tz.full ? (
              <Row
                icon={Globe2}
                label="Fuseau horaire"
                value={<span title={timezoneFull(brand.timezone)}>{tz.full}</span>}
                valueTestId="sd-timezone"
              />
            ) : null;
          })()}
          <Row icon={Building2} label="Département" value={brand?.name} valueTestId="sd-brand" />
          <Row
            icon={MapPin}
            label="Site"
            value={
              site ? (
                <>
                  <span className="font-medium">{site.name}</span>
                  {site.address && (
                    <span className="block text-xs text-zinc-600 font-mono mt-0.5">{site.address}</span>
                  )}
                </>
              ) : null
            }
            valueTestId="sd-site"
          />
          <Row
            icon={Tag}
            label="Catégorie"
            value={
              category ? (
                <span className="inline-flex items-center gap-2">
                  <span className="h-2 w-2 rounded-full" style={{ backgroundColor: category.color || "#0055FF" }} />
                  {category.name}
                </span>
              ) : null
            }
            valueTestId="sd-category"
          />
          {shift.role_label && (
            <Row icon={Briefcase} label="Poste / Rôle" value={shift.role_label} valueTestId="sd-role" />
          )}
          {shift.notes && (
            <Row icon={FileText} label="Consignes / Commentaires" value={<span className="whitespace-pre-wrap">{shift.notes}</span>} valueTestId="sd-notes" />
          )}

          {/* Pauses planifiées */}
          {Array.isArray(shift.breaks) && shift.breaks.length > 0 && (() => {
            const total = shiftMinutes(shift.start_time, shift.end_time);
            const unpaid = shift.breaks.reduce((acc, b) => acc + (b.is_paid ? 0 : shiftMinutes(b.start_time, b.end_time)), 0);
            const effective = Math.max(total - unpaid, 0);
            return (
              <div className="border-t border-zinc-100 pt-4" data-testid="sd-breaks">
                <div className="flex items-start gap-3">
                  <PauseIcon size={14} className="text-amber-600 mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="label-uppercase mb-2">Pauses planifiées ({shift.breaks.length})</p>
                    <ul className="space-y-1.5">
                      {shift.breaks.map((b, idx) => (
                        <li key={idx} className="text-sm text-zinc-900 flex items-center gap-2 flex-wrap" data-testid={`sd-break-${idx}`}>
                          <span className="font-mono text-xs px-1.5 py-0.5 rounded-sm bg-amber-50 border border-amber-200 text-amber-800">
                            {b.start_time} → {b.end_time}
                          </span>
                          {b.label && <span className="text-zinc-700">{b.label}</span>}
                          <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded-sm border ${b.is_paid ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-zinc-100 border-zinc-300 text-zinc-700"}`}>
                            {b.is_paid ? "Rémunérée" : "Non rémunérée"}
                          </span>
                        </li>
                      ))}
                    </ul>
                    <div className="mt-3 grid grid-cols-2 gap-2 text-[11px] font-mono">
                      <div className="bg-zinc-50 border border-zinc-200 rounded-sm p-2">
                        <p className="text-zinc-500">Durée totale</p>
                        <p className="text-zinc-900 text-sm font-semibold">{formatMinutes(total)}</p>
                      </div>
                      <div className="bg-amber-50 border border-amber-200 rounded-sm p-2">
                        <p className="text-amber-700">Heures effectives</p>
                        <p className="text-amber-900 text-sm font-semibold" data-testid="sd-effective-hours">{formatMinutes(effective)}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}
        </div>
        <DialogFooter className="px-6 py-4 border-t border-zinc-100 bg-white">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="sd-close-btn">Fermer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
